/**
 * ThemeProvider - Global Theme Context for THE ONESCENT DIAL
 * 
 * PROPRIETARY NOTICE: THE ONESCENT DIAL
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Provides theme state management and CSS variable injection for
 * dark/light mode support throughout the application.
 * 
 * USAGE:
 * - Wrap app with <ThemeProvider>
 * - Use useTheme() hook to access theme state and toggle
 * - CSS variables automatically applied to :root
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { THEME_VARIANTS, generateThemeCSS } from '../utils/styleCard';

// Create context
const ThemeContext = createContext(null);

/**
 * ThemeProvider Component
 * Manages theme state and injects CSS variables
 */
export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState(() => {
    // Load from localStorage or use default
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_VARIANTS.config.storageKey);
      if (stored && THEME_VARIANTS[stored]) {
        return stored;
      }
    }
    return THEME_VARIANTS.config.default;
  });
  
  // Get current theme object
  const theme = THEME_VARIANTS[themeId] || THEME_VARIANTS.dark;
  const isDark = themeId === 'dark';
  const isLight = themeId === 'light';
  
  // Inject CSS variables when theme changes
  useEffect(() => {
    // Generate and inject CSS
    const css = generateThemeCSS(themeId);
    
    // Find or create style element
    let styleEl = document.getElementById('tod-theme-vars');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'tod-theme-vars';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;
    
    // Set data attribute on both body and html for CSS selectors
    document.body.setAttribute('data-theme', themeId);
    document.documentElement.setAttribute('data-theme', themeId);
    
    // Apply meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme.colors?.bg || (themeId === 'dark' ? '#020204' : '#FAF7F2'));
    }
    
    // Save to localStorage
    localStorage.setItem(THEME_VARIANTS.config.storageKey, themeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId]); // theme.colors?.bg derived from themeId, not needed as dependency
  
  // Cycle through all available themes: dark → light → high-contrast → dark
  const cycleTheme = useCallback(() => {
    setThemeId(prev => {
      const themes = THEME_VARIANTS.config.availableThemes || ['dark', 'light', 'high-contrast'];
      const currentIdx = themes.indexOf(prev);
      const nextIdx = (currentIdx + 1) % themes.length;
      const newTheme = themes[nextIdx];
      return newTheme;
    });
  }, []);
  
  // Toggle between dark and light (legacy - still useful for simple toggle)
  const toggleTheme = useCallback(() => {
    setThemeId(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      return newTheme;
    });
  }, []);
  
  // Set specific theme
  const setTheme = useCallback((newThemeId) => {
    if (THEME_VARIANTS[newThemeId]) {
      setThemeId(newThemeId);
    } else {
      console.warn(`[ThemeContext] Unknown theme: ${newThemeId}`);
    }
  }, []);
  
  // Check if high contrast mode is active
  const isHighContrast = themeId === 'high-contrast';
  
  // Context value
  const value = {
    themeId,
    theme,
    isDark,
    isLight,
    isHighContrast,
    toggleTheme,
    cycleTheme,
    setTheme,
    themes: THEME_VARIANTS,
    availableThemes: THEME_VARIANTS.config.availableThemes || ['dark', 'light', 'high-contrast']
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme Hook
 * Access theme state and controls from any component
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * ThemeToggle Component
 * A ready-to-use toggle button for switching themes
 */
export const ThemeToggle = ({ 
  className = '',
  showLabel = true,
  size = 'md' // 'sm' | 'md' | 'lg'
}) => {
  const { isDark, toggleTheme, theme } = useTheme();
  
  const sizes = {
    sm: { icon: 14, padding: 'p-1.5' },
    md: { icon: 18, padding: 'p-2' },
    lg: { icon: 22, padding: 'p-2.5' }
  };
  
  const { icon: iconSize, padding } = sizes[size] || sizes.md;
  
  return (
    <button
      onClick={toggleTheme}
      className={`
        flex items-center gap-2 ${padding} rounded transition-all duration-300
        bg-[var(--tod-bg-card)] border border-[var(--tod-border-secondary)]
        hover:border-[var(--tod-border-focus)] hover:bg-[var(--tod-bg-secondary)]
        text-[var(--tod-text-secondary)] hover:text-[var(--tod-brass)]
        ${className}
      `}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      data-testid="theme-toggle-btn"
    >
      {/* Sun/Moon Icon */}
      {isDark ? (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/>
          <line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      ) : (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      )}
      
      {showLabel && (
        <span className="text-xs font-medium uppercase tracking-wider hidden sm:inline">
          {theme.name}
        </span>
      )}
    </button>
  );
};

/**
 * withTheme HOC
 * Wrap a component to receive theme props
 */
export const withTheme = (Component) => {
  return function ThemedComponent(props) {
    const themeProps = useTheme();
    return <Component {...props} {...themeProps} />;
  };
};

export default ThemeProvider;
